import AppBar from '@mui/material/AppBar';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useState } from 'react';
import { Student, UnitAssessment } from 'types';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import LoadingScreen from '@lwtears/lwt-common-frontend/lib/LoadingScreen';
import { NotepadSrc } from 'components/common/NotepadSrc';
import UnitAssessmentReports from 'components/modules/ClassReports/Components/UnitAssessmentReports';
import useStudentAssessmentStore from 'graph/state/query-hooks/use-student-assessment-scores';
import HideableTabPanel from '../../../common/HideableTabPanel';
import StudentScoreRow from './StudentScoreRow';

type Props = {
  students: Student[];
  curriculumId: number;
  classId: number;
  unitAssessments: UnitAssessment[];
  visible: boolean;
};

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    appBar: {
      boxShadow: 'none',
    },
    subTabs: {
      '& .MuiTabs-indicator': {
        backgroundColor: '#DC20AD',
        height: '3px',
      },
      '& .Mui-selected': {
        color: '#DC20AD !important',
      },
      '& .MuiTab-root': {
        fontSize: '1.1rem',
        fontWeight: 'bold',
        paddingBottom: 0,
        maxWidth: 'unset',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
      },
    },
    studentList: {
      '& .MuiTableCell-root': {
        border: `1px solid ${(theme.palette as any).grey[600]}`,
        padding: theme.spacing(2),
      },
    },
    floatLeft: {
      float: 'left',
      marginRight: theme.spacing(2),
    },
    p4: {
      padding: theme.spacing(4),
    },
  };
});

const StudentUnitList = ({
  classId,
  curriculumId,
  students = [],
  unitAssessments = [],
  visible,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const [tabSelection, setTabSelection] = useState<string | false>('Scores');

  const studentIds = students.map((s) => s.id) ?? [];
  const assessmentIds = unitAssessments.map((ua) => ua.id) ?? [];

  const [{ studentAssessments = [], isLoading }] = useStudentAssessmentStore(
    studentIds,
    assessmentIds
  );

  if (students.length === 0) {
    return (
      <Typography variant="body1" align="center" className={classes.p4}>
        {t('class.studentsNone')}
      </Typography>
    );
  }

  if (isLoading) return <LoadingScreen backgroundImage={NotepadSrc} />;

  return (
    <>
      <AppBar position="static" color="default" className={classes.appBar}>
        <Tabs
          value={visible ? tabSelection : false}
          onChange={(_, value) => setTabSelection(value)}
          className={classes.subTabs}
        >
          <Tab label={t('class.studentScores')} value="Scores" />
          <Tab label={t('class.studentReport')} value="Report" />
        </Tabs>
      </AppBar>
      <HideableTabPanel index="Scores" value={tabSelection}>
        <TableContainer className={classes.studentList}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">
                    <b>{t('class.studentsName')}</b>
                  </Typography>
                </TableCell>
                {unitAssessments.map((unitAssessment) => (
                  <TableCell key={`${unitAssessment.id}-${unitAssessment.unit.unitNumber}`}>
                    <Grid container alignContent="center" justifyContent="center">
                      <Typography variant="body1">
                        <b>{`${t('class.unit')} ${unitAssessment.unit.unitNumber}`}</b>
                      </Typography>
                    </Grid>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => (
                <StudentScoreRow
                  key={student.id}
                  student={student}
                  classId={classId}
                  curriculumId={curriculumId}
                  assessmentIds={assessmentIds}
                  studentAssessments={studentAssessments.filter((s) => s.studentId === student.id)}
                  returnTab="Unit"
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </HideableTabPanel>
      <HideableTabPanel index="Report" value={tabSelection}>
        <UnitAssessmentReports
          students={students}
          curriculumId={curriculumId}
          classroomId={classId}
        />
      </HideableTabPanel>
    </>
  );
};

export default StudentUnitList;
